// A floating window that can be dragged by a child marked `[data-drag-handle]`
// and resized by children marked `[data-resize-handle]` (value "nw"|"ne"|"sw"|"se"
// — which corner). CSS positions it (position: fixed, anchored top/left); dragging
// applies a translate offset on top of that, and resizing sets the element's
// width/height (and, for the top/left corners, adjusts the offset so the opposite
// corner stays put). Both are clamped to a min size and the viewport. Elements
// marked `[data-no-drag]` (e.g. the close button) don't start a drag.
const MIN_WIDTH = 360;
const MIN_HEIGHT = 420;
const MARGIN = 12;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

class DraggableWindow extends HTMLElement {
  connectedCallback() {
    this.offsetX = 0;
    this.offsetY = 0;
    this.dragging = false;
    this.resizing = false;

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);

    this.addEventListener("pointerdown", this._onPointerDown);
  }

  disconnectedCallback() {
    this.removeEventListener("pointerdown", this._onPointerDown);
    window.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
  }

  _onPointerDown(e) {
    if (e.button !== 0) return;

    const resizeHandle = e.target.closest("[data-resize-handle]");
    if (resizeHandle && this.contains(resizeHandle)) {
      e.preventDefault();
      this.resizing = true;
      this.corner = resizeHandle.getAttribute("data-resize-handle") || "se";
      const rect = this.getBoundingClientRect();
      this.startRect = rect;
      this.startOffsetX = this.offsetX;
      this.startOffsetY = this.offsetY;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this._listen();
      return;
    }

    const handle = e.target.closest("[data-drag-handle]");
    if (!handle || !this.contains(handle)) return;
    if (e.target.closest("[data-no-drag]")) return;

    e.preventDefault();
    this.dragging = true;
    this.startX = e.clientX - this.offsetX;
    this.startY = e.clientY - this.offsetY;
    this._listen();
  }

  _onPointerMove(e) {
    if (this.resizing) {
      this._resize(e);
    } else if (this.dragging) {
      this._drag(e);
    }
  }

  _resize(e) {
    const rect = this.startRect;
    const west = this.corner.includes("w");
    const north = this.corner.includes("n");
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;

    const maxWidth = west
      ? rect.right - MARGIN
      : window.innerWidth - rect.left - MARGIN;
    const maxHeight = north
      ? rect.bottom - MARGIN
      : window.innerHeight - rect.top - MARGIN;
    const width = clamp(rect.width + (west ? -dx : dx), MIN_WIDTH, maxWidth);
    const height = clamp(
      rect.height + (north ? -dy : dy),
      MIN_HEIGHT,
      maxHeight,
    );

    // For the top/left corners the opposite edge is fixed, so move the window by
    // however much the size actually changed (respecting the clamps above).
    this.offsetX = west
      ? this.startOffsetX + (rect.width - width)
      : this.startOffsetX;
    this.offsetY = north
      ? this.startOffsetY + (rect.height - height)
      : this.startOffsetY;

    this.style.width = `${width}px`;
    this.style.height = `${height}px`;
    this.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px)`;
  }

  _drag(e) {
    this.offsetX = e.clientX - this.startX;
    this.offsetY = e.clientY - this.startY;
    this.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px)`;

    // Keep enough of the window on screen that the header stays grabbable.
    const rect = this.getBoundingClientRect();
    let dx = 0;
    let dy = 0;
    if (rect.right < MARGIN) dx = MARGIN - rect.right;
    if (rect.left > window.innerWidth - MARGIN)
      dx = window.innerWidth - MARGIN - rect.left;
    if (rect.top < 0) dy = -rect.top;
    if (rect.top > window.innerHeight - MARGIN)
      dy = window.innerHeight - MARGIN - rect.top;
    if (dx || dy) {
      this.offsetX += dx;
      this.offsetY += dy;
      this.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px)`;
    }
  }

  _onPointerUp() {
    this.dragging = false;
    this.resizing = false;
    window.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
  }

  _listen() {
    window.addEventListener("pointermove", this._onPointerMove);
    window.addEventListener("pointerup", this._onPointerUp);
  }
}

if (!customElements.get("draggable-window")) {
  customElements.define("draggable-window", DraggableWindow);
}
